import { useEffect, useRef } from "react";

/* Scroll reveal. Adds .in when the element enters, then .done once the
   entrance has played so the stagger delays retire and hovers never lag. */
export function useReveal() {
  const refs = useRef([]);

  useEffect(() => {
    const els = refs.current.filter(Boolean);
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("in");
          io.unobserve(e.target);
          setTimeout(() => e.target.classList.add("done"), 1400);
        }),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (el) => {
    if (el && !refs.current.includes(el)) refs.current.push(el);
  };
}

/* Pause every looping animation on hidden tabs.
   animation-play-state is not inherited, so the rule in landing.css
   targets elements and pseudo-elements explicitly. */
export function usePauseOnHide() {
  useEffect(() => {
    const fn = () =>
      document.body.classList.toggle("paused", document.hidden);
    document.addEventListener("visibilitychange", fn);
    return () => {
      document.removeEventListener("visibilitychange", fn);
      document.body.classList.remove("paused");
    };
  }, []);
}

/* ────────────────────────────────────────────────────────────
   IL FILO — the golden thread.
   One hairline that draws itself as you scroll, threading the page
   from the roastery in Trieste down to the call to action.
   ──────────────────────────────────────────────────────────── */
export function Filo({ d, width = 120, height = 200, top = -100, dot }) {
  const ref = useRef(null);

  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const path = svg.querySelector("path");
    if (!path) return;

    const len = path.getTotalLength();
    svg.style.setProperty("--len", len + "");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let raf = null;
    let last = -1;
    let visible = false;

    const draw = () => {
      raf = null;
      const rect = svg.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(
        1,
        Math.max(0, (vh - rect.top - vh * 0.16) / (rect.height + vh * 0.34)),
      );
      if (Math.abs(p - last) < 0.006) return;
      last = p;
      svg.style.setProperty("--draw", p.toFixed(3));
    };
    const onScroll = () => {
      if (raf === null && visible) raf = requestAnimationFrame(draw);
    };

    const pin = () => {
      svg.style.setProperty("--draw", "1");
    };

    if (reduce.matches) {
      pin();
    } else {
      const io = new IntersectionObserver(
        ([e]) => {
          visible = e.isIntersecting;
          if (visible) onScroll();
        },
        { threshold: 0 },
      );
      io.observe(svg);
      window.addEventListener("scroll", onScroll, { passive: true });
      draw();

      const onPref = (e) => {
        if (e.matches) {
          window.removeEventListener("scroll", onScroll);
          pin();
        }
      };
      reduce.addEventListener("change", onPref);

      return () => {
        io.disconnect();
        window.removeEventListener("scroll", onScroll);
        reduce.removeEventListener("change", onPref);
        if (raf !== null) cancelAnimationFrame(raf);
      };
    }
  }, []);

  return (
    <svg
      className="filo"
      ref={ref}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ top }}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
      {dot && <circle cx={dot[0]} cy={dot[1]} r="3" />}
    </svg>
  );
}
